import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Container,
  Divider,
  FormControlLabel,
  Grid,
  LinearProgress,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Checkbox,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import LinkIcon from '@mui/icons-material/Link';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Helmet } from 'react-helmet-async';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import Avatar from '@mui/material/Avatar';

import config from '@src/config';
import { toast } from '@src/components/core/toaster';
import { fetchClient } from '@src/api/actions/clients';
import defaultClient from '@src/api/clients/default';
import leadGurusLogo from '@src/assets/logo-full.png';
import { useAuth } from '@src/context';
import paths from '@src/paths';

const CRM_OPTIONS = [
  { value: 'builder-prime', label: 'Builder Prime' },
  { value: 'i360', label: 'Improveit360 (i360)' },
  { value: 'jobnimbus', label: 'JobNimbus' },
  { value: 'leadconduit', label: 'LeadConduit' },
  { value: 'leadperfection', label: 'LeadPerfection' },
  { value: 'marketsharp', label: 'MarketSharp' },
  { value: 'opta', label: 'Opta' },
  { value: 'salesforce', label: 'Salesforce' },
  { value: 'servicetitan', label: 'ServiceTitan' },
  { value: 'other', label: 'Other' },
  { value: 'none', label: 'No CRM' },
];

const VERTICAL_OPTIONS = ['Bath', 'Roofing', 'Solar', 'Windows', 'HVAC', 'Flooring', 'Gutters'];

const DEFAULT_STATE = {
  basics: {
    firstName: '',
    lastName: '',
    email: '',
    position: '',
    secondaryPocs: [],
    launchGoal: null,
  },
  contract: {
    docuhubLink: '',
    file: null,
    deleteExisting: false,
  },
  territories: {
    text: '',
    fileName: '',
    parsed: [],
  },
  verticals: {
    selections: [],
    applyToAllZips: true,
  },
  assets: {
    files: [],
    folderLink: '',
    removedIds: [],
  },
  messaging: {
    salesSchedule: '',
    evergreenOffer: '',
    financingOffer: '',
  },
  billingAcknowledged: false,
  billingContact: {
    name: '',
    email: '',
    phone: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    postalCode: '',
    country: '',
  },
  crm: {
    choice: '',
    other: '',
    usesLeadConduit: '',
    leadConduitNotes: '',
    crmPostingNotes: '',
    wantsGoogleSheet: false,
    emailNotifications: false,
    notificationEmails: '',
    postingFiles: [],
    removedPostingDocIds: [],
  },
  approvals: {
    adPreviewUrl: '',
    adStatus: 'pending',
    adNotes: '',
    landingUrl: '',
    landingStatus: 'pending',
    landingNotes: '',
    adApproverName: '',
    adApproverEmail: '',
    landingApproverName: '',
    landingApproverEmail: '',
  },
};

const ZONE_TEMPLATE_ROWS = [
  { zip: '60601', territory: 'Downtown' },
  { zip: '60614', territory: 'North' },
];

const SectionCard = React.forwardRef(function SectionCard({ title, action, children, subheader }, ref) {
  return (
    <Card ref={ref}>
      <CardHeader title={title} subheader={subheader} action={action} />
      <CardContent>
        <Stack spacing={3}>{children}</Stack>
      </CardContent>
    </Card>
  );
});

function StatusRow({ label, complete }) {
  const Icon = complete ? CheckCircleIcon : RadioButtonUncheckedIcon;
  return (
    <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
      <Icon color={complete ? 'success' : 'disabled'} fontSize="small" />
      <Typography color={complete ? 'text.primary' : 'text.secondary'} variant="body2">
        {label}
      </Typography>
    </Stack>
  );
}

function OnboardingFlowPage() {
  const { clientSlug = 'new-client' } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { state: authState } = useAuth();
  const user = authState?.user;
  const [state, setState] = useState(DEFAULT_STATE);
  const [client, setClient] = useState(null);
  const [isLoadingClient, setIsLoadingClient] = useState(false);
  const [isClientError, setIsClientError] = useState(false);
  const [guidedMode, setGuidedMode] = useState(false);
  const [guidedStepIndex, setGuidedStepIndex] = useState(0);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteFirstName, setInviteFirstName] = useState('');
  const [inviteLastName, setInviteLastName] = useState('');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [contractDownloadUrl, setContractDownloadUrl] = useState(null);
  const [assetDownloadUrls, setAssetDownloadUrls] = useState([]);
  const [postingDownloadUrls, setPostingDownloadUrls] = useState([]);
  const [isLoadingOnboarding, setIsLoadingOnboarding] = useState(false);

  const memberships = useMemo(
    () => (user?.client_memberships ? [...user.client_memberships] : []),
    [user?.client_memberships]
  );
  const isStaff = user?.is_staff || user?.is_superuser;
  const statusAllowed = useMemo(() => {
    if (isStaff) return true;
    return client?.status ? ['new_business', 'onboarding'].includes(client.status) : true;
  }, [client?.status, isStaff]);
  const isClientMember = useMemo(() => {
    if (isStaff || !clientSlug) {
      return isStaff;
    }
    return memberships.some((m) => {
      const slug = String(clientSlug);
      const membershipSlug = m?.client_slug ? String(m.client_slug) : '';
      const membershipId = m?.client_id !== undefined && m?.client_id !== null ? String(m.client_id) : '';
      return slug === membershipSlug || slug === membershipId;
    });
  }, [clientSlug, isStaff, memberships]);

  const contractRef = useRef(null);
  const contactsRef = useRef(null);
  const assetsRef = useRef(null);
  const offersRef = useRef(null);
  const routingRef = useRef(null);
  const salesRef = useRef(null);
  const launchRef = useRef(null);
  const approvalsRef = useRef(null);
  const billingRef = useRef(null);

  const closeInviteModal = useCallback(() => {
    setInviteModalOpen(false);
  }, []);

  const openInviteModal = useCallback(() => {
    setInviteModalOpen(true);
  }, []);

  const handleInvite = useCallback(() => {
    if (!client?.id) {
      toast.error('Load the client before inviting members.');
      return;
    }
    if (!inviteEmail) {
      toast.error('Email is required to invite a member.');
      return;
    }
    setInviteLoading(true);
    defaultClient
      .request({
        url: 'accounts/auth/client-memberships/invite/',
        method: 'POST',
        payload: {
          client_id: client.id,
          email: inviteEmail,
          first_name: inviteFirstName,
          last_name: inviteLastName,
        },
      })
      .then(() => {
        toast.success('Invite sent. We emailed them a link to join.');
        setInviteEmail('');
        setInviteFirstName('');
        setInviteLastName('');
        closeInviteModal();
      })
      .catch(() => {
        toast.error('Unable to send invite. Please try again.');
      })
      .finally(() => {
        setInviteLoading(false);
      });
  }, [client?.id, closeInviteModal, inviteEmail, inviteFirstName, inviteLastName]);

  useEffect(() => {
    if (user && !isStaff && (!isClientMember || !statusAllowed)) {
      navigate(paths.notAuthorized, { replace: true });
    }
  }, [isClientMember, isStaff, navigate, statusAllowed, user]);

  useEffect(() => {
    if (!user) {
      return;
    }

    if (!isClientMember) {
      setClient(null);
      setIsClientError(true);
      return;
    }

    setState(DEFAULT_STATE);
    if (!clientSlug) {
      setClient(null);
      return;
    }

    setIsLoadingClient(true);
    setIsClientError(false);
    fetchClient(clientSlug)
      .then((response) => {
        setClient(response?.data || null);
        const desiredDate = response?.data?.desired_launch_date
          ? dayjs(response.data.desired_launch_date)
          : null;
        setState((prev) => ({
          ...prev,
          basics: {
            ...prev.basics,
            launchGoal: desiredDate && desiredDate.isValid() ? desiredDate : null,
          },
        }));
      })
      .catch(() => {
        setClient(null);
        setIsClientError(true);
      })
      .finally(() => {
        setIsLoadingClient(false);
      });
  }, [clientSlug, isClientMember, user]);

  useEffect(() => {
    if (client?.slug && clientSlug && clientSlug !== client.slug) {
      navigate(`/onboarding/${client.slug}${location.search}`, { replace: true });
    }
  }, [client?.slug, clientSlug, location.search, navigate]);

  useEffect(() => {
    const zipsParam = searchParams.get('zips');
    const verticalsParam = searchParams.get('verticals');
    setState((prev) => {
      const next = { ...prev, verticals: { ...prev.verticals }, territories: { ...prev.territories } };
      if (verticalsParam) {
        next.verticals.selections = verticalsParam.split(',').filter(Boolean);
      }
      if (zipsParam) {
        try {
          const parsed = JSON.parse(zipsParam);
          const text = parsed
            .map((row) => [row.zip, row.territory].filter(Boolean).join(', '))
            .join('\n');
          next.territories = { ...next.territories, parsed, text };
        } catch {
          // ignore
        }
      }
      return next;
    });
  }, [searchParams]);

  useEffect(() => {
    if (state.contract?.file instanceof File) {
      const url = URL.createObjectURL(state.contract.file);
      setContractDownloadUrl(url);
      return () => URL.revokeObjectURL(url);
    }
    if (state.contract?.file?.url) {
      setContractDownloadUrl(state.contract.file.url);
    } else {
      setContractDownloadUrl(null);
    }
    return undefined;
  }, [state.contract?.file]);

  useEffect(() => {
    const files = state.assets?.files || [];
    const urls = files
      .map((file) => {
        if (file instanceof File) {
          return { name: file.name, url: URL.createObjectURL(file) };
        }
        if (file?.url) {
          return { name: file.name || file.url, url: file.url };
        }
        return null;
      })
      .filter(Boolean);
    setAssetDownloadUrls(urls);
    return () => {
      urls.forEach((entry) => {
        if (entry.url?.startsWith('blob:')) {
          URL.revokeObjectURL(entry.url);
        }
      });
    };
  }, [state.assets?.files]);

  useEffect(() => {
    const files = state.crm?.postingFiles || [];
    const urls = files
      .map((file) => {
        if (file instanceof File) {
          return { name: file.name, url: URL.createObjectURL(file) };
        }
        if (file?.url) {
          return { name: file.name || file.url, url: file.url };
        }
        return null;
      })
      .filter(Boolean);
    setPostingDownloadUrls(urls);
    return () => {
      urls.forEach((entry) => {
        if (entry.url?.startsWith('blob:')) {
          URL.revokeObjectURL(entry.url);
        }
      });
    };
  }, [state.crm?.postingFiles]);

  useEffect(() => {
    if (!clientSlug || !isClientMember) return;
    setIsLoadingOnboarding(true);
    defaultClient
      .request({ url: `clients/${clientSlug}/onboarding/`, method: 'GET' })
      .then((resp) => {
        const data = resp?.data || {};
        setState((prev) => ({
          ...prev,
          billingAcknowledged: data.billing_acknowledged || false,
          billingContact: {
            name: data.billing_contact?.name || '',
            email: data.billing_contact?.email || '',
            phone: data.billing_contact?.phone || '',
            addressLine1: data.billing_contact?.addressLine1 || '',
            addressLine2: data.billing_contact?.addressLine2 || '',
            city: data.billing_contact?.city || '',
            state: data.billing_contact?.state || '',
            postalCode: data.billing_contact?.postalCode || '',
            country: data.billing_contact?.country || '',
          },
          basics: {
            ...prev.basics,
            firstName: data.basics?.firstName || prev.basics.firstName,
            lastName: data.basics?.lastName || prev.basics.lastName,
            email: data.basics?.email || prev.basics.email,
            position: data.basics?.position || prev.basics.position,
          },
          crm: {
            ...prev.crm,
            choice: data.crm_choice || prev.crm.choice,
            other: data.crm_other || prev.crm.other,
            wantsGoogleSheet: data.crm_wants_google_sheet || prev.crm.wantsGoogleSheet,
            emailNotifications: data.crm_email_notifications || prev.crm.emailNotifications,
            notificationEmails: data.crm_notification_emails || prev.crm.notificationEmails,
            leadConduitNotes: data.crm_posting_notes || prev.crm.leadConduitNotes,
            postingFiles: (data.posting_docs || []).map((f) => ({ id: f.id, name: f.name, url: f.url })),
            removedPostingDocIds: [],
          },
          messaging: {
            ...prev.messaging,
            salesSchedule: data.sales_schedule || prev.messaging.salesSchedule,
            evergreenOffer: data.evergreen_offer || prev.messaging.evergreenOffer,
            financingOffer: data.financing_offer || prev.messaging.financingOffer,
          },
          assets: {
            ...prev.assets,
            files: (data.assets || []).map((f) => ({ id: f.id, name: f.name, url: f.url })),
            folderLink: data.assets_folder_link || prev.assets.folderLink,
            removedIds: [],
          },
          approvals: {
            ...prev.approvals,
            adPreviewUrl: data.ad_preview_url || prev.approvals.adPreviewUrl,
            landingUrl: data.landing_url || prev.approvals.landingUrl,
            adStatus: data.ad_status || prev.approvals.adStatus,
            landingStatus: data.landing_status || prev.approvals.landingStatus,
            adNotes: data.ad_notes || prev.approvals.adNotes,
            landingNotes: data.landing_notes || prev.approvals.landingNotes,
            adApproverName: data.ad_approver_name || prev.approvals.adApproverName,
            adApproverEmail: data.ad_approver_email || prev.approvals.adApproverEmail,
            landingApproverName: data.landing_approver_name || prev.approvals.landingApproverName,
            landingApproverEmail: data.landing_approver_email || prev.approvals.landingApproverEmail,
          },
          contract: {
            ...prev.contract,
          },
        }));
        if (data.secondary_pocs) {
          setState((prev) => ({
            ...prev,
            basics: {
              ...prev.basics,
              secondaryPocs: data.secondary_pocs.map((poc) => ({
                firstName: poc.firstName || '',
                lastName: poc.lastName || '',
                email: poc.email || '',
                position: poc.position || '',
              })),
            },
          }));
        }
        if (data.launch_goal) {
          const launchDate = dayjs(data.launch_goal);
          setState((prev) => ({
            ...prev,
            basics: { ...prev.basics, launchGoal: launchDate.isValid() ? launchDate : null },
          }));
        }
        if (data.contract?.length) {
          const contractFile = data.contract[0];
          setState((prev) => ({
            ...prev,
            contract: {
              ...prev.contract,
              file: { id: contractFile.id, name: contractFile.name, url: contractFile.url },
              deleteExisting: false,
            },
          }));
          setContractDownloadUrl(contractFile.url);
        }
      })
      .catch(() => {
        toast.error('Could not load onboarding data.');
      })
      .finally(() => setIsLoadingOnboarding(false));
  }, [clientSlug, isClientMember]);

  const completion = useMemo(() => {
    const {
      contract,
      territories,
      verticals,
      assets,
      messaging,
      billingAcknowledged,
      crm,
      approvals,
      offers,
    } = state;

    return {
      basics: Boolean(
        state.basics.firstName &&
          state.basics.lastName &&
          state.basics.email &&
          state.basics.position
      ),
      contract: Boolean(contract.file),
      territories: Boolean(territories.parsed.length || territories.text.trim()),
      verticals: Boolean(verticals.selections.length),
      assets: Boolean(assets.files.length || assets.folderLink.trim()),
      offers: Boolean(messaging.evergreenOffer.trim() && messaging.financingOffer?.trim()),
      messaging: Boolean(messaging.salesSchedule.trim()),
      billing: billingAcknowledged,
      launch: Boolean(state.basics.launchGoal),
      crm: Boolean(
        crm.choice &&
          ((crm.choice !== 'none' && crm.leadConduitNotes.trim()) ||
            (crm.choice === 'none' && (crm.leadConduitNotes.trim() || crm.wantsGoogleSheet)))
      ),
      approvals: Boolean(
        approvals.adStatus !== 'pending' &&
          approvals.landingStatus !== 'pending' &&
          (approvals.adPreviewUrl.trim() || approvals.landingUrl.trim())
      ),
    };
  }, [state]);

  const completedCount = useMemo(
    () => Object.values(completion).filter(Boolean).length,
    [completion]
  );
  const progressValue = useMemo(
    () => Math.round((completedCount / Object.keys(completion).length) * 100),
    [completedCount, completion]
  );

  const guidedSteps = useMemo(
    () => [
      { key: 'billing', label: 'Billing', ref: billingRef, complete: completion.billing },
      { key: 'basics', label: 'Client contacts', ref: contactsRef, complete: completion.basics },
      { key: 'assets', label: 'Creative assets', ref: assetsRef, complete: completion.assets },
      { key: 'offers', label: 'Offers', ref: offersRef, complete: completion.offers },
      { key: 'crm', label: 'Lead routing & notifications', ref: routingRef, complete: completion.crm },
      { key: 'messaging', label: 'Sales schedule', ref: salesRef, complete: completion.messaging },
      { key: 'launch', label: 'Target launch', ref: launchRef, complete: completion.launch },
      { key: 'approvals', label: 'Approvals', ref: approvalsRef, complete: completion.approvals },
    ],
    [completion, billingRef, contactsRef, assetsRef, routingRef, salesRef, launchRef, approvalsRef]
  );

  const goToStep = (index) => {
    setGuidedMode(true);
    setGuidedStepIndex(index);
    const target = guidedSteps[index]?.ref?.current;
    if (target?.scrollIntoView) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const goToNextStep = () => {
    const nextIndex =
      guidedSteps.findIndex((step, idx) => !step.complete && idx >= guidedStepIndex) !== -1
        ? guidedSteps.findIndex((step, idx) => !step.complete && idx >= guidedStepIndex)
        : guidedSteps.findIndex((step) => !step.complete);
    if (nextIndex >= 0) {
      goToStep(nextIndex);
    } else {
      goToStep(guidedSteps.length - 1);
    }
  };

  const handleFileChange = (field, files, allowMultiple = false) => {
    if (!files || !files.length) return;

    setState((prev) => ({
      ...prev,
      [field]: {
        ...prev[field],
        files: allowMultiple ? Array.from(files) : prev[field]?.files || [],
        file: allowMultiple ? undefined : files[0],
        fileName: allowMultiple ? undefined : files[0]?.name || '',
        deleteExisting: field === 'contract' ? false : prev[field]?.deleteExisting,
      },
    }));
  };


  const parseZips = (text) => {
    return text
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        const [zip = '', territory = ''] = line.split(/[,|\t]/);
        return { zip: zip.trim(), territory: territory.trim() };
      })
      .filter((row) => row.zip);
  };

  const handleZipTextChange = (value) => {
    setState((prev) => ({
      ...prev,
      territories: {
        ...prev.territories,
        text: value,
        parsed: parseZips(value),
      },
    }));
  };

  const handleZipFile = (files) => {
    if (!files || !files.length) return;
    const [file] = files;
    const reader = new FileReader();
    reader.onload = (event) => {
      const contents = event.target?.result || '';
      setState((prev) => ({
        ...prev,
        territories: {
          ...prev.territories,
          text: contents,
          parsed: parseZips(String(contents)),
          fileName: file.name,
        },
      }));
    };
    reader.readAsText(file);
  };

  const handleSave = async () => {
    if (!clientSlug) return;
    const form = new FormData();
    form.append('billing_acknowledged', state.billingAcknowledged ? 'true' : 'false');
    form.append('billing_contact', JSON.stringify(state.billingContact || {}));
    form.append('billing_contact_name', state.billingContact.name || '');
    form.append('billing_contact_email', state.billingContact.email || '');
    form.append('billing_contact_phone', state.billingContact.phone || '');
    form.append('billing_contact_addressLine1', state.billingContact.addressLine1 || '');
    form.append('billing_contact_addressLine2', state.billingContact.addressLine2 || '');
    form.append('billing_contact_city', state.billingContact.city || '');
    form.append('billing_contact_state', state.billingContact.state || '');
    form.append('billing_contact_postalCode', state.billingContact.postalCode || '');
    form.append('billing_contact_country', state.billingContact.country || '');
    form.append(
      'basics',
      JSON.stringify({
        firstName: state.basics.firstName,
        lastName: state.basics.lastName,
        email: state.basics.email,
        position: state.basics.position,
      })
    );
    form.append('secondary_pocs', JSON.stringify(state.basics.secondaryPocs || []));
    form.append('sales_schedule', state.messaging.salesSchedule || '');
    form.append('evergreen_offer', state.messaging.evergreenOffer || '');
    form.append('financing_offer', state.messaging.financingOffer || '');
    form.append('crm_posting_notes', state.crm.leadConduitNotes || '');
    form.append('crm_choice', state.crm.choice || '');
    form.append('crm_other', state.crm.other || '');
    form.append('crm_wants_google_sheet', state.crm.wantsGoogleSheet ? 'true' : 'false');
    form.append('crm_email_notifications', state.crm.emailNotifications ? 'true' : 'false');
    form.append('crm_notification_emails', state.crm.notificationEmails || '');
    form.append('assets_folder_link', state.assets.folderLink || '');
    form.append('ad_preview_url', state.approvals.adPreviewUrl || '');
    form.append('landing_url', state.approvals.landingUrl || '');
    form.append('ad_status', state.approvals.adStatus || 'pending');
    form.append('landing_status', state.approvals.landingStatus || 'pending');
    form.append('ad_notes', state.approvals.adNotes || '');
    form.append('landing_notes', state.approvals.landingNotes || '');
    form.append('ad_approver_name', state.approvals.adApproverName || '');
    form.append('ad_approver_email', state.approvals.adApproverEmail || '');
    form.append('landing_approver_name', state.approvals.landingApproverName || '');
    form.append('landing_approver_email', state.approvals.landingApproverEmail || '');
    if (state.contract.deleteExisting) {
      form.append('delete_contract', 'true');
    }
    if (state.assets.removedIds.length) {
      form.append('remove_asset_ids', JSON.stringify(state.assets.removedIds));
    }
    if (state.crm.removedPostingDocIds.length) {
      form.append('remove_posting_doc_ids', JSON.stringify(state.crm.removedPostingDocIds));
    }
    if (state.basics.launchGoal) {
      const formatted = dayjs(state.basics.launchGoal).isValid()
        ? dayjs(state.basics.launchGoal).format('YYYY-MM-DD')
        : state.basics.launchGoal;
      form.append('launch_goal', formatted);
    }

    if (state.contract?.file instanceof File) {
      form.append('contract', state.contract.file);
    }
    (state.assets.files || [])
      .filter((f) => f instanceof File)
      .forEach((file) => form.append('assets', file));
    (state.crm.postingFiles || [])
      .filter((f) => f instanceof File)
      .forEach((file) => form.append('posting_docs', file));

    try {
      await defaultClient.request({
        url: `clients/${clientSlug}/onboarding/`,
        method: 'PATCH',
        payload: form,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Onboarding saved.');
      // Reload to pull canonical values/URLs
      const resp = await defaultClient.request({ url: `clients/${clientSlug}/onboarding/`, method: 'GET' });
      const data = resp?.data || {};
      setState((prev) => ({
        ...prev,
        billingAcknowledged: data.billing_acknowledged || false,
        billingContact: {
          name: data.billing_contact?.name || '',
          email: data.billing_contact?.email || '',
          phone: data.billing_contact?.phone || '',
          addressLine1: data.billing_contact?.addressLine1 || '',
          addressLine2: data.billing_contact?.addressLine2 || '',
          city: data.billing_contact?.city || '',
          state: data.billing_contact?.state || '',
          postalCode: data.billing_contact?.postalCode || '',
          country: data.billing_contact?.country || '',
        },
        basics: {
          ...prev.basics,
          firstName: data.basics?.firstName || prev.basics.firstName,
          lastName: data.basics?.lastName || prev.basics.lastName,
          email: data.basics?.email || prev.basics.email,
          position: data.basics?.position || prev.basics.position,
          secondaryPocs: prev.basics.secondaryPocs,
          launchGoal:
            data.launch_goal && dayjs(data.launch_goal).isValid()
              ? dayjs(data.launch_goal)
              : prev.basics.launchGoal,
        },
        crm: {
          ...prev.crm,
          choice: data.crm_choice || prev.crm.choice,
          other: data.crm_other || prev.crm.other,
          wantsGoogleSheet: data.crm_wants_google_sheet || prev.crm.wantsGoogleSheet,
          emailNotifications: data.crm_email_notifications || prev.crm.emailNotifications,
          notificationEmails: data.crm_notification_emails || prev.crm.notificationEmails,
          leadConduitNotes: data.crm_posting_notes || prev.crm.leadConduitNotes,
          postingFiles: (data.posting_docs || []).map((f) => ({ id: f.id, name: f.name, url: f.url })),
          removedPostingDocIds: [],
        },
        messaging: {
          ...prev.messaging,
          salesSchedule: data.sales_schedule || prev.messaging.salesSchedule,
          evergreenOffer: data.evergreen_offer || prev.messaging.evergreenOffer,
          financingOffer: data.financing_offer || prev.messaging.financingOffer,
        },
        assets: {
          ...prev.assets,
          files: (data.assets || []).map((f) => ({ id: f.id, name: f.name, url: f.url })),
          folderLink: data.assets_folder_link || prev.assets.folderLink,
          removedIds: [],
        },
        approvals: {
          ...prev.approvals,
          adPreviewUrl: data.ad_preview_url || prev.approvals.adPreviewUrl,
          landingUrl: data.landing_url || prev.approvals.landingUrl,
          adStatus: data.ad_status || prev.approvals.adStatus,
          landingStatus: data.landing_status || prev.approvals.landingStatus,
          adNotes: data.ad_notes || prev.approvals.adNotes,
          landingNotes: data.landing_notes || prev.approvals.landingNotes,
          adApproverName: data.ad_approver_name || prev.approvals.adApproverName,
          adApproverEmail: data.ad_approver_email || prev.approvals.adApproverEmail,
          landingApproverName: data.landing_approver_name || prev.approvals.landingApproverName,
          landingApproverEmail: data.landing_approver_email || prev.approvals.landingApproverEmail,
        },
        contract: {
          ...prev.contract,
          deleteExisting: false,
        },
      }));
      if (data.secondary_pocs) {
        setState((prev) => ({
          ...prev,
          basics: {
            ...prev.basics,
            secondaryPocs: data.secondary_pocs.map((poc) => ({
              firstName: poc.firstName || '',
              lastName: poc.lastName || '',
              email: poc.email || '',
              position: poc.position || '',
            })),
          },
        }));
      }
      if (data.contract?.length) {
        const contractFile = data.contract[0];
        setState((prev) => ({
          ...prev,
          contract: {
            ...prev.contract,
            file: { id: contractFile.id, name: contractFile.name, url: contractFile.url },
            deleteExisting: false,
          },
        }));
        setContractDownloadUrl(contractFile.url);
      }
    } catch (error) {
      toast.error('Could not save onboarding.');
    }
  };

  const updateState = (path, value) => {
    setState((prev) => {
      const next = { ...prev };
      const segments = path.split('.');
      let cursor = next;
      segments.slice(0, -1).forEach((segment) => {
        cursor[segment] = { ...cursor[segment] };
        cursor = cursor[segment];
      });
      cursor[segments.slice(-1)[0]] = value;
      return next;
    });
  };

  const updateSecondaryPoc = (index, field, value) => {
    setState((prev) => {
      const next = { ...prev, basics: { ...prev.basics, secondaryPocs: [...(prev.basics.secondaryPocs || [])] } };
      next.basics.secondaryPocs[index] = {
        ...(next.basics.secondaryPocs[index] || { firstName: '', lastName: '', email: '', position: '' }),
        [field]: value,
      };
      return next;
    });
  };

  const addSecondaryPoc = () => {
    setState((prev) => ({
      ...prev,
      basics: {
        ...prev.basics,
        secondaryPocs: [...(prev.basics.secondaryPocs || []), { firstName: '', lastName: '', email: '', position: '' }],
      },
    }));
  };

  const removeSecondaryPoc = (index) => {
    setState((prev) => {
      const next = { ...prev, basics: { ...prev.basics, secondaryPocs: [...(prev.basics.secondaryPocs || [])] } };
      next.basics.secondaryPocs.splice(index, 1);
      return next;
    });
  };

  const handleCrmPostingFiles = (files) => {
    if (!files || !files.length) return;
    setState((prev) => ({
      ...prev,
      crm: {
        ...prev.crm,
        postingFiles: Array.from(files),
        removedPostingDocIds: prev.crm.removedPostingDocIds || [],
      },
    }));
  };

  const removeAssetFile = (index) => {
    setState((prev) => {
      const nextFiles = [...(prev.assets.files || [])];
      const [removed] = nextFiles.splice(index, 1);
      const removedIds = [...(prev.assets.removedIds || [])];
      if (removed?.id) {
        removedIds.push(removed.id);
      }
      return {
        ...prev,
        assets: {
          ...prev.assets,
          files: nextFiles,
          removedIds,
        },
      };
    });
  };

  const removePostingDoc = (index) => {
    setState((prev) => {
      const nextFiles = [...(prev.crm.postingFiles || [])];
      const [removed] = nextFiles.splice(index, 1);
      const removedIds = [...(prev.crm.removedPostingDocIds || [])];
      if (removed?.id) {
        removedIds.push(removed.id);
      }
      return {
        ...prev,
        crm: {
          ...prev.crm,
          postingFiles: nextFiles,
          removedPostingDocIds: removedIds,
        },
      };
    });
  };

  const clearContractFile = () => {
    setState((prev) => ({
      ...prev,
      contract: {
        ...prev.contract,
        file: null,
        deleteExisting: Boolean(prev.contract.file?.id),
      },
    }));
    setContractDownloadUrl(null);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      <Helmet>
        <title>Client Onboarding | {config.SITE.title}</title>
      </Helmet>

      <Stack spacing={3} direction={{ xs: 'column', lg: 'row' }} alignItems="flex-start">
        <Box
          sx={{
            position: { lg: 'sticky', xs: 'static' },
            top: 32,
            minWidth: { lg: 260, xs: '100%' },
            maxWidth: { lg: 260, xs: '100%' },
            alignSelf: 'flex-start',
          }}
        >
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Stack spacing={1}>
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                  <RocketLaunchIcon color="primary" fontSize="small" />
                  <Typography variant="subtitle2">Launch readiness</Typography>
                </Stack>
                <Chip color="primary" label={`${progressValue}%`} size="small" />
                <LinearProgress
                  aria-label="Launch readiness progress"
                  sx={{ height: 8, borderRadius: 999 }}
                  value={progressValue}
                  variant="determinate"
                />
              </Stack>
            </CardContent>
          </Card>
          <Card variant="outlined">
            <CardHeader
              title="Guided mode"
              action={
                <Button
                  size="small"
                  variant={guidedMode ? 'contained' : 'outlined'}
                  onClick={() => {
                    setGuidedMode(!guidedMode);
                    if (!guidedMode) {
                      const firstIncomplete = guidedSteps.findIndex((step) => !step.complete);
                      goToStep(firstIncomplete >= 0 ? firstIncomplete : 0);
                    }
                  }}
                >
                  {guidedMode ? 'Exit' : 'Start'}
                </Button>
              }
            />
            <CardContent>
              <Stack spacing={1}>
                {guidedSteps.map((step, idx) => (
                  <Button
                    key={step.key}
                    variant={idx === guidedStepIndex ? 'contained' : 'text'}
                    color={step.complete ? 'success' : 'primary'}
                    fullWidth
                    onClick={() => goToStep(idx)}
                    sx={{
                      justifyContent: 'space-between',
                      textTransform: 'none',
                    }}
                  >
                    <Typography variant="body2">{step.label}</Typography>
                    <Chip
                      size="small"
                      label={step.complete ? 'Done' : idx === guidedStepIndex ? 'Current' : 'Pending'}
                      color={step.complete ? 'success' : idx === guidedStepIndex ? 'primary' : 'default'}
                    />
                  </Button>
                ))}
                {guidedMode && (
                  <Button variant="outlined" onClick={goToNextStep}>
                    Next incomplete
                  </Button>
                )}
              </Stack>
            </CardContent>
          </Card>
          <Stack spacing={1} sx={{ mt: 2 }}>
            <Button
              variant="contained"
              startIcon={<CheckCircleIcon />}
              onClick={handleSave}
              fullWidth
            >
              Save progress
            </Button>
            <Button
              variant="text"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              fullWidth
            >
              Back to top
            </Button>
          </Stack>
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack
            direction="row"
            justifyContent={isStaff ? 'space-between' : 'flex-end'}
            sx={{ mb: 2 }}
            alignItems="center"
          >
            {isStaff ? (
              <Button
                variant="text"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate(paths.onboarding.list)}
              >
                Back to onboarding list
              </Button>
            ) : null}
            <Button variant="outlined" onClick={openInviteModal}>
              Invite team members
            </Button>
          </Stack>
          <Dialog open={inviteModalOpen} onClose={closeInviteModal} fullWidth maxWidth="sm">
            <DialogTitle>Invite team members</DialogTitle>
            <DialogContent>
              <Grid container spacing={2} sx={{ mt: 0.5 }}>
                <Grid item xs={12}>
                  <TextField
                    label="Email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    fullWidth
                    size="small"
                    autoFocus
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="First name"
                    value={inviteFirstName}
                    onChange={(e) => setInviteFirstName(e.target.value)}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Last name"
                    value={inviteLastName}
                    onChange={(e) => setInviteLastName(e.target.value)}
                    fullWidth
                    size="small"
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={closeInviteModal}>Cancel</Button>
              <Button variant="contained" onClick={handleInvite} disabled={inviteLoading}>
                {inviteLoading ? 'Sending...' : 'Send invite'}
              </Button>
            </DialogActions>
          </Dialog>
          <Card>
            <CardContent>
              <Stack spacing={3}>
                <Box
                  sx={{
                    background: 'linear-gradient(135deg, #f66f39, #f88f5f)',
                    borderRadius: 2,
                    color: 'common.white',
                    p: { xs: 2, md: 3 },
                    boxShadow: 4,
                  }}
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    sx={{ alignItems: { md: 'center', xs: 'flex-start' }, justifyContent: 'space-between' }}
                  >
                    <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
                      <Box
                        component="img"
                        src={leadGurusLogo}
                        alt="Lead Gurus"
                        sx={{
                          height: 48,
                          width: 'auto',
                          objectFit: 'contain',
                          filter: 'drop-shadow(0 6px 12px rgba(0,0,0,0.2))',
                        }}
                      />
                      <Stack spacing={0.5}>
                        <Typography variant="h5" sx={{ fontWeight: 800 }}>
                          Lead Gurus
                        </Typography>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          Onboarding
                        </Typography>
                      </Stack>
                    </Stack>
                    <Stack direction="row" spacing={2} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                      {client?.client_logo_url || client?.client_logo_file ? (
                        <Box
                          component="img"
                          src={client?.client_logo_url || client?.client_logo_file}
                          alt={client?.name || 'Client logo'}
                          sx={{
                            maxHeight: 64,
                            maxWidth: 180,
                            objectFit: 'contain',
                            filter: 'drop-shadow(0 6px 12px rgba(0,0,0,0.15))',
                            bgcolor: 'rgba(255,255,255,0.2)',
                            borderRadius: 1,
                            p: 1,
                          }}
                        />
                      ) : (
                        <Avatar
                          sx={{
                            width: 64,
                            height: 64,
                            boxShadow: 2,
                            bgcolor: 'white',
                            color: 'text.primary',
                            fontWeight: 700,
                          }}
                          variant="circular"
                        >
                          {client?.name ? client.name.slice(0, 2).toUpperCase() : 'CL'}
                        </Avatar>
                      )}
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {client?.name || 'Client'}
                      </Typography>
                    </Stack>
                  </Stack>
                </Box>

                <Stack spacing={1}>
                  <Typography color="text.secondary" variant="overline">
                    Onboarding flow
                  </Typography>
                  <Typography variant="h4">
                    {client?.name ? `Onboarding for ${client.name}` : 'Onboarding checklist'}
                  </Typography>
                  {isLoadingClient ? (
                    <Typography color="text.secondary" sx={{ mt: 1 }} variant="caption">
                      Loading client details…
                    </Typography>
                  ) : null}
                    {isClientError ? (
                      <Alert sx={{ mt: 1 }} severity="warning">
                        Could not load client or you do not have access to this client.
                      </Alert>
                    ) : null}
                </Stack>
              </Stack>
            </CardContent>
          </Card>

          <Stack spacing={3} sx={{ mt: 3 }}>
            <SectionCard
              ref={contractRef}
              title="Contract"
              subheader="Upload the signed agreement for this client."
            >
              <Stack spacing={2}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={6}>
                    <Button component="label" startIcon={<UploadFileIcon />} variant="contained">
                      Upload signed contract
                      <input
                        hidden
                        type="file"
                        accept=".pdf,.doc,.docx"
                        onChange={(e) => handleFileChange('contract', e.target.files)}
                      />
                    </Button>
                    {state.contract.file && (
                      <Stack direction="row" spacing={1} sx={{ mt: 1, alignItems: 'center' }}>
                        <Typography variant="body2">Attached: {state.contract.file.name}</Typography>
                        {contractDownloadUrl ? (
                          <Button
                            component="a"
                            href={contractDownloadUrl}
                            download={state.contract.file.name}
                            size="small"
                            variant="outlined"
                          >
                            Download
                          </Button>
                        ) : null}
                        <Button color="error" onClick={clearContractFile} size="small">
                          Remove
                        </Button>
                      </Stack>
                    )}
                  </Grid>
                </Grid>
              </Stack>
            </SectionCard>

            <SectionCard
              ref={billingRef}
              title="Billing"
              subheader="Share billing contact details and place the billing card on file."
            >
              <Stack spacing={2}>
                <Card variant="outlined">
                  <CardContent>
                    <Stack spacing={2}>
                      <Typography variant="subtitle2">Billing contact for invoicing</Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                            label="Name"
                            value={state.billingContact.name}
                            onChange={(e) => updateState('billingContact.name', e.target.value)}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                            label="Email"
                            type="email"
                            value={state.billingContact.email}
                            onChange={(e) => updateState('billingContact.email', e.target.value)}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                            label="Phone"
                            value={state.billingContact.phone}
                            onChange={(e) => updateState('billingContact.phone', e.target.value)}
                          />
                        </Grid>
                      </Grid>
                      <Typography variant="subtitle2">Billing address (for invoices)</Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Address line 1"
                            value={state.billingContact.addressLine1}
                            onChange={(e) => updateState('billingContact.addressLine1', e.target.value)}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Address line 2"
                            value={state.billingContact.addressLine2}
                            onChange={(e) => updateState('billingContact.addressLine2', e.target.value)}
                          />
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <TextField
                            fullWidth
                            label="City"
                            value={state.billingContact.city}
                            onChange={(e) => updateState('billingContact.city', e.target.value)}
                          />
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <TextField
                            fullWidth
                            label="State / Region"
                            value={state.billingContact.state}
                            onChange={(e) => updateState('billingContact.state', e.target.value)}
                          />
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <TextField
                            fullWidth
                            label="Postal code"
                            value={state.billingContact.postalCode}
                            onChange={(e) => updateState('billingContact.postalCode', e.target.value)}
                          />
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <TextField
                            fullWidth
                            label="Country"
                            value={state.billingContact.country}
                            onChange={(e) => updateState('billingContact.country', e.target.value)}
                          />
                        </Grid>
                      </Grid>
                    </Stack>
                  </CardContent>
                </Card>
                <Alert severity="info">
                  Please Note: This card will automatically be charged for the media fees each month as outlined in the agreement. Ad spend is billed by Facebook directly to the card on your ad account.
                </Alert>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ alignItems: 'center' }}>
                  <Button
                    color="primary"
                    href="https://buy.stripe.com/28o03g6gAh2hgz6fZe"
                    rel="noreferrer"
                    target="_blank"
                    variant="outlined"
                  >
                    Open Stripe billing link
                  </Button>
                  <Typography color="text.secondary" variant="body2">
                    Use this link to add the billing card on file.
                  </Typography>
                </Stack>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={state.billingAcknowledged}
                      onChange={(e) => updateState('billingAcknowledged', e.target.checked)}
                    />
                  }
                  label="Credit card on file via Stripe billing link"
                />
              </Stack>
            </SectionCard>

            <SectionCard
              ref={contactsRef}
              title="Client contacts"
              subheader="Tell us who we’ll work with on your team."
            >
              <Alert severity="info" sx={{ mb: 2 }}>
                Please add the team members we’ll be in regular contact with. Add any secondary contacts by clicking
                “Add secondary POC.”
              </Alert>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Main POC
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        label="First name"
                        value={state.basics.firstName}
                        onChange={(e) => updateState('basics.firstName', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        label="Last name"
                        value={state.basics.lastName}
                        onChange={(e) => updateState('basics.lastName', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        label="Email"
                        type="email"
                        value={state.basics.email}
                        onChange={(e) => updateState('basics.email', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        label="Position/title"
                        value={state.basics.position}
                        onChange={(e) => updateState('basics.position', e.target.value)}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <Divider />
                  <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2">Secondary POCs</Typography>
                    <Button size="small" variant="outlined" onClick={addSecondaryPoc}>
                      Add secondary POC
                    </Button>
                  </Stack>
                  {(state.basics.secondaryPocs || []).map((poc, idx) => (
                    <Grid container spacing={2} key={`secondary-poc-${idx}`} sx={{ mb: 1 }}>
                      <Grid item xs={12} md={3}>
                        <TextField
                          fullWidth
                          label="First name"
                          value={poc.firstName}
                          onChange={(e) => updateSecondaryPoc(idx, 'firstName', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          fullWidth
                          label="Last name"
                          value={poc.lastName}
                          onChange={(e) => updateSecondaryPoc(idx, 'lastName', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          fullWidth
                          label="Email"
                          type="email"
                          value={poc.email}
                          onChange={(e) => updateSecondaryPoc(idx, 'email', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <TextField
                          fullWidth
                          label="Position"
                          value={poc.position}
                          onChange={(e) => updateSecondaryPoc(idx, 'position', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} md={1} sx={{ display: 'flex', alignItems: 'center' }}>
                        <Button color="error" onClick={() => removeSecondaryPoc(idx)} size="small">
                          Remove
                        </Button>
                      </Grid>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            </SectionCard>

        <SectionCard
          ref={assetsRef}
          title="Creative assets"
          subheader="Upload assets or share a folder link."
        >
          <Alert severity="info" sx={{ mb: 2 }}>
            Please upload as many raw assets as you have, especially video of your installers and product. We tailor ads
            to your brand, and we’ll sift through the raw creative to pick what will work best for campaign performance.
          </Alert>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Card variant="outlined" sx={{ borderStyle: 'dashed', borderColor: 'grey.300' }}>
                <CardContent>
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    sx={{ alignItems: { md: 'center', xs: 'flex-start' }, justifyContent: 'space-between' }}
                  >
                    <Stack spacing={1}>
                      <Typography variant="subtitle2">Upload creative assets</Typography>
                      <Typography color="text.secondary" variant="body2">
                        Drag files into the button or click to select (images, video, zip). We keep a short list
                        below once attached.
                      </Typography>
                      <Button component="label" startIcon={<UploadFileIcon />} variant="contained" color="primary">
                        Upload assets
                        <input
                          hidden
                          multiple
                          type="file"
                          onChange={(e) => handleFileChange('assets', e.target.files, true)}
                        />
                      </Button>
                      {state.assets.files.length > 0 && (
                        <Stack spacing={0.5}>
                          {state.assets.files.map((file, idx) => {
                            const url = assetDownloadUrls[idx]?.url;
                            const name = file?.name || `File ${idx + 1}`;
                            return (
                              <Stack
                                key={`${name}-${idx}`}
                                direction="row"
                                spacing={1}
                                sx={{ alignItems: 'center', flexWrap: 'wrap' }}
                              >
                                <Typography variant="body2">{name}</Typography>
                                {url ? (
                                  <Button component="a" href={url} download={name} size="small" variant="outlined">
                                    Download
                                  </Button>
                                ) : null}
                                <Button color="error" onClick={() => removeAssetFile(idx)} size="small">
                                  Remove
                                </Button>
                              </Stack>
                            );
                          })}
                        </Stack>
                      )}
                    </Stack>
                    <TextField
                      fullWidth
                      label="Asset folder link"
                      placeholder="Dropbox, Google Drive, etc."
                      value={state.assets.folderLink}
                      onChange={(e) => updateState('assets.folderLink', e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LinkIcon color="disabled" fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </SectionCard>

        <SectionCard ref={offersRef} title="Offers" subheader="Confirm the evergreen and financing offers.">
          <Stack spacing={2}>
            <Alert severity="info">
              High-percentage offers (e.g., 50% off install) almost always beat dollar amounts or complex BOGOs. Strong,
              evergreen offers win more often on Meta. Seasonal/monthly promos force frequent creative resets that can
              hurt performance, so we require an evergreen offer to stay competitive.
            </Alert>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Evergreen offer"
                  placeholder="e.g. 50% off install"
                  value={state.messaging.evergreenOffer}
                  onChange={(e) => updateState('messaging.evergreenOffer', e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Financing offer"
                  placeholder="e.g. 12 months no interest, $0 down"
                  value={state.messaging.financingOffer || ''}
                  onChange={(e) => updateState('messaging.financingOffer', e.target.value)}
                />
              </Grid>
            </Grid>
          </Stack>
        </SectionCard>

        <SectionCard
          ref={routingRef}
          title="Lead routing & notifications"
          subheader="Tell us how to post leads and who should receive notifications."
        >
          <Alert severity="info" sx={{ mb: 2 }}>
            Tell us where we’ll send leads. If certain lead types or verticals require different endpoints or paths,
            call that out. For LeadPerfection delivery, we’ll need an `srs_id` tied to the `productid` field to ensure
            proper attribution and credit.
          </Alert>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Where will be sending leads to?"
                value={state.crm.choice}
                onChange={(e) => updateState('crm.choice', e.target.value)}
              >
                {CRM_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            {state.crm.choice === 'other' && (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Other CRM name"
                  value={state.crm.other}
                  onChange={(e) => updateState('crm.other', e.target.value)}
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                fullWidth
                sx={{ mt: 1.5 }}
                label="CRM posting instructions"
                placeholder="Paste posting steps, links, or notes."
                minRows={4}
                multiline
                value={state.crm.leadConduitNotes}
                onChange={(e) => updateState('crm.leadConduitNotes', e.target.value)}
                helperText="You can paste links to docs or portals here for flexibility. If needed, include the exact syntax for product fields so we can map them correctly into your CRM."
              />
            </Grid>
            <Grid item xs={12}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ alignItems: 'flex-start' }}>
                  <Button component="label" startIcon={<UploadFileIcon />} variant="outlined">
                    Attach posting docs
                    <input
                      hidden
                    type="file"
                    multiple
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.txt"
                    onChange={(e) => handleCrmPostingFiles(e.target.files)}
                  />
                </Button>
                <Typography color="text.secondary" variant="body2">
                  Upload any posting instructions or examples (multiple files allowed).
                </Typography>
              </Stack>
              {state.crm.postingFiles?.length ? (
                <Stack spacing={0.5} sx={{ mt: 1 }}>
                  {state.crm.postingFiles.map((file, idx) => {
                    const url = postingDownloadUrls[idx]?.url;
                    const name = file?.name || `File ${idx + 1}`;
                    return (
                      <Stack
                        key={`${name}-${idx}`}
                        direction="row"
                        spacing={1}
                        sx={{ alignItems: 'center', flexWrap: 'wrap' }}
                      >
                        <Typography variant="body2">{name}</Typography>
                        {url ? (
                          <Button component="a" href={url} download={name} size="small" variant="outlined">
                            Download
                          </Button>
                        ) : null}
                        <Button color="error" onClick={() => removePostingDoc(idx)} size="small">
                          Remove
                        </Button>
                      </Stack>
                    );
                  })}
                </Stack>
              ) : null}
            </Grid>
            {state.crm.choice === 'none' && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="If no CRM, should we create a Google Sheet shell?"
                  helperText="We can provide the sheet; ongoing support is out of scope."
                  value={state.crm.wantsGoogleSheet ? 'Yes - create a sheet' : 'No'}
                  InputProps={{ readOnly: true }}
                />
                <Button
                  sx={{ mt: 1 }}
                  variant="outlined"
                  onClick={() => updateState('crm.wantsGoogleSheet', !state.crm.wantsGoogleSheet)}
                >
                  {state.crm.wantsGoogleSheet ? 'Undo Google Sheet request' : 'Request Google Sheet'}
                </Button>
              </Grid>
            )}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={state.crm.emailNotifications}
                    onChange={(e) => updateState('crm.emailNotifications', e.target.checked)}
                  />
                }
                label="Send email notifications for leads"
              />
              {state.crm.emailNotifications && (
                <TextField
                  fullWidth
                  sx={{ mt: 1 }}
                  label="Notification recipients"
                  placeholder="name@company.com, ops@company.com"
                  value={state.crm.notificationEmails}
                  onChange={(e) => updateState('crm.notificationEmails', e.target.value)}
                />
              )}
            </Grid>
          </Grid>
        </SectionCard>

        <SectionCard
          ref={salesRef}
          title="Sales schedule for self-booking"
          subheader="When can leads self-book with your team?"
        >
          <Stack spacing={2}>
            <Alert severity="info">
              Our self-booking flow lets leads request a preferred time; it does not sync to your calendar or booking
              system. We tell them it’s a request only, that your call center will confirm, and if they cannot confirm,
              the appointment will be canceled.
            </Alert>
            <TextField
              fullWidth
              label="Sales schedule"
              placeholder="e.g. Mon - Sat 10, 2, 6"
              value={state.messaging.salesSchedule}
              onChange={(e) => updateState('messaging.salesSchedule', e.target.value)}
            />
          </Stack>
        </SectionCard>

        <SectionCard ref={launchRef} title="Target launch" subheader="When do you want to go live?">
          <Stack spacing={2}>
            <Alert severity="info">
              Campaigns are typically ready 7–10 business days after the contract is signed. Delays in sharing
              creative assets or approvals can push this window.
            </Alert>
            <DatePicker
              label="Target launch date"
              value={state.basics.launchGoal}
              onChange={(newValue) => updateState('basics.launchGoal', newValue)}
              slotProps={{ textField: { fullWidth: true, placeholder: 'Pick a date' } }}
            />
          </Stack>
        </SectionCard>

        <SectionCard
          ref={approvalsRef}
          title="Approvals"
          subheader="Guru will drop in assets for review. Clients approve or request edits (one round)."
        >
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'grey.50' }}>
                <Chip label="Guru adds" size="small" sx={{ mb: 1 }} />
                <Typography fontWeight={600} variant="subtitle1">
                  Ads for approval
                </Typography>
                <TextField
                  fullWidth
                  label="Ads preview link"
                  placeholder="Paste the preview URL when ready"
                  value={state.approvals.adPreviewUrl}
                  onChange={(e) => updateState('approvals.adPreviewUrl', e.target.value)}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'grey.50' }}>
                <Chip label="Guru adds" size="small" sx={{ mb: 1 }} />
                <Typography fontWeight={600} variant="subtitle1">
                  Landing page approval
                </Typography>
                <TextField
                  fullWidth
                  label="Landing page link"
                  placeholder="Paste landing page link when ready"
                  value={state.approvals.landingUrl}
                  onChange={(e) => updateState('approvals.landingUrl', e.target.value)}
                />
              </Box>
            </Grid>

            {(state.approvals.adPreviewUrl || state.approvals.landingUrl) && (
              <Grid item xs={12}>
                <Alert severity="info">
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    Before we dive in, here’s some quick context. Our approach is shaped by hundreds of millions in home-improvement ad spend, and everything here is built on what’s working best right now. These concepts follow a proven framework designed to drive performance from the start.
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    As you review, remember the primary goal of these ads is lead generation — branding is secondary. The creative is built to get responses first, with brand feel layered in.
                  </Typography>
                  <Typography variant="body2">
                    Your input matters most on messaging and offer accuracy. The style, structure, and editing follow a system that consistently delivers results. We include one round of revisions, so please consolidate any notes into a single pass.
                  </Typography>
                </Alert>
              </Grid>
            )}

            <Grid item xs={12} md={6}>
              <Box sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'grey.200' }}>
                <Chip label="Client approval" size="small" sx={{ mb: 1 }} />
                <ToggleButtonGroup
                  fullWidth
                  sx={{ mb: 1 }}
                  exclusive
                  disabled={!state.approvals.adPreviewUrl}
                  value={state.approvals.adStatus}
                  onChange={(e, value) => updateState('approvals.adStatus', value || 'pending')}
                  size="small"
                >
                  <ToggleButton value="pending">Pending</ToggleButton>
                  <ToggleButton value="approved">Approved</ToggleButton>
                  <ToggleButton value="needs-edits">Needs edits</ToggleButton>
                </ToggleButtonGroup>
                {state.approvals.adStatus === 'approved' && (
                  <Grid container spacing={1} sx={{ mb: 1 }}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Approver name"
                        placeholder="Client name"
                        value={state.approvals.adApproverName}
                        onChange={(e) => updateState('approvals.adApproverName', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Approver email"
                        type="email"
                        placeholder="name@client.com"
                        value={state.approvals.adApproverEmail}
                        onChange={(e) => updateState('approvals.adApproverEmail', e.target.value)}
                      />
                    </Grid>
                  </Grid>
                )}
                {state.approvals.adStatus === 'needs-edits' && (
                  <TextField
                    fullWidth
                    sx={{ mb: 1 }}
                    label="Ad feedback (one round of revisions)"
                    minRows={3}
                    multiline
                    value={state.approvals.adNotes}
                    onChange={(e) => updateState('approvals.adNotes', e.target.value)}
                  />
                )}
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'grey.200' }}>
                <Chip label="Client approval" size="small" sx={{ mb: 1 }} />
                <ToggleButtonGroup
                  fullWidth
                  sx={{ mb: 1 }}
                  exclusive
                  disabled={!state.approvals.landingUrl}
                  value={state.approvals.landingStatus}
                  onChange={(e, value) => updateState('approvals.landingStatus', value || 'pending')}
                  size="small"
                >
                  <ToggleButton value="pending">Pending</ToggleButton>
                  <ToggleButton value="approved">Approved</ToggleButton>
                  <ToggleButton value="needs-edits">Needs edits</ToggleButton>
                </ToggleButtonGroup>
                {state.approvals.landingStatus === 'approved' && (
                  <Grid container spacing={1} sx={{ mb: 1 }}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Approver name"
                        placeholder="Client name"
                        value={state.approvals.landingApproverName}
                        onChange={(e) => updateState('approvals.landingApproverName', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Approver email"
                        type="email"
                        placeholder="name@client.com"
                        value={state.approvals.landingApproverEmail}
                        onChange={(e) => updateState('approvals.landingApproverEmail', e.target.value)}
                      />
                    </Grid>
                  </Grid>
                )}
                {state.approvals.landingStatus === 'needs-edits' && (
                  <TextField
                    fullWidth
                    sx={{ mb: 1 }}
                    label="Landing page feedback"
                    minRows={3}
                    multiline
                    value={state.approvals.landingNotes}
                    onChange={(e) => updateState('approvals.landingNotes', e.target.value)}
                  />
                )}
              </Box>
            </Grid>
          </Grid>
        </SectionCard>

          </Stack>
        </Box>
      </Stack>
    </Container>
  );
}

OnboardingFlowPage.displayName = 'OnboardingFlowPage';
export default OnboardingFlowPage;
